import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Self-hosted variable font (plan §5.2 — no external font CDN).
// Family name is "Inter Variable" (see theme.css --font-sans). Italic is
// a separate face; without it, em/italic synthesizes a slant.
import '@fontsource-variable/inter'
import '@fontsource-variable/inter/wght-italic.css'
import './styles/globals.css'

// Generated by @tanstack/router-plugin from the routes/ tree.
import { routeTree } from './routeTree.gen'
import { installPointerFocusGuard } from './lib/pointerFocus'
import {
  DeferredNotFound,
  DeferredRouteError,
} from '@/components/layout/deferredRouteChrome'
import { ApiError } from '@/lib/apiClient'

installPointerFocusGuard()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (
          error instanceof ApiError &&
          [400, 401, 403, 404].includes(error.status)
        ) {
          return false
        }
        return failureCount < 2
      },
    },
  },
})

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultNotFoundComponent: DeferredNotFound,
  defaultErrorComponent: DeferredRouteError,
  context: { queryClient },
})

// Type-safe router registration.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

declare module '@tanstack/router-core' {
  interface Register {
    router: typeof router
  }
}

/**
 * Dev runs against the MSW mock (plan §10) unless VITE_API_MOCK=false —
 * Phase 7 integration flips that to hit the real ALB. The worker must be
 * ready before render so the auth bootstrap's /auth/refresh is intercepted.
 */
async function enableMocking() {
  if (!import.meta.env.DEV || import.meta.env.VITE_API_MOCK === 'false') return
  const { worker } = await import('./mocks/browser')
  await worker.start({ onUnhandledRequest: 'bypass' })
}

enableMocking().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  )
})
